import React, { memo } from "react";
import PropTypes from "prop-types";
import ListItemText from "@mui/material/ListItemText";
import { equals } from "ramda";
import { getThumbnailPath } from "~/libs/image";
import { styled } from "@mui/material/styles";

import ScrollIntoViewIfNeeded from "~/components/ScrollIntoViewIfNeeded";
import ListItemButton from "@mui/material/ListItemButton";

const StyledListItemButton = styled(ListItemButton)(({ theme, selected }) => ({
  padding: theme.spacing(2, 4),
  flexDirection: "column",
  cursor: selected ? "default" : "pointer",
  pointerEvents: selected ? "none" : "auto",
  "&:focus-visible": {
    // "--dsb-outline-color": "green",
    outline: theme.outline(),
    outlineOffset: "-2px"
  }
}));

const StyledListItemText = styled(ListItemText)(() => ({
  margin: 0,
  "&.MuiListItemText-primary": {
    variant: "body1"
  }
}));

const Wrapper = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(1.5)
}));

const Ratio = styled("div")(props => ({
  position: "relative",
  width: "35%",
  height: "auto",
  border: "1px solid black",
  "& + &": {
    left: -1
  },
  "&::after": {
    content: '""',
    display: "block",
    height: 0,
    width: "100%",
    paddingBottom: `${props.aspectRatio}%`
  },
  "& > *": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "block"
  }
}));

const Dummy = styled("div")(props => ({
  position: "relative",
  width: "35%",
  height: "auto",
  border: "1px solid transparent",
  "& + &": {
    left: -1
  },
  "&::after": {
    content: '""',
    display: "block",
    height: 0,
    width: "100%",
    paddingBottom: `${props.aspectRatio}%`
  }
}));

function PagePreview({ pages, isActive, onClick, classes, humanReadablePages, isFirstPage, isLastPage, aspectRatio }) {
  // FIXME: first and last pages are always a single entry...
  const isDoublePage = pages.length !== 1;
  let label = "";

  if (isDoublePage) {
    label = "Seiten ";
  } else {
    label = "Seite ";
  }

  label += humanReadablePages.join(" / ");

  return (
    <ScrollIntoViewIfNeeded active={isActive}>
      <StyledListItemButton onClick={onClick} selected={isActive}>
        <Wrapper>
          {isDoublePage && isFirstPage && <Dummy aspectRatio={aspectRatio} />}

          {pages.map(page => (
            <Ratio key={page} aspectRatio={aspectRatio}>
              <img className="lazyload" data-sizes="auto" data-src={getThumbnailPath(page)} alt={label} />
            </Ratio>
          ))}

          {isDoublePage && isLastPage && <Dummy aspectRatio={aspectRatio} />}
        </Wrapper>
        <StyledListItemText primary={label} />
      </StyledListItemButton>
    </ScrollIntoViewIfNeeded>
  );
}

PagePreview.whyDidYouRender = false;

PagePreview.propTypes = {
  pages: PropTypes.array,
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
  classes: PropTypes.object,
  humanReadablePages: PropTypes.array,
  isFirstPage: PropTypes.bool,
  isLastPage: PropTypes.bool
};

function areEqual(prevProps, nextProps) {
  return (
    equals(prevProps.aspectRatio, nextProps.aspectRatio) &&
    equals(prevProps.isActive, nextProps.isActive) &&
    equals(prevProps.pages, nextProps.pages)
  );
}

const Memoized = memo(PagePreview, areEqual);

export default Memoized;
